/**
 * Header — barra superior do app (GUPM-158, extraído do `AppShell`).
 *
 * Alinha o topo ao v5.10 do operador: marca + chips de status A/B/C
 * (consolidado + por barragem, com Observação) + banner de tiltímetro sem
 * sinal + contatos de emergência. Sem nenhuma ação de escrita de dado.
 *
 * Container: injeta os hooks (`useDamScope`, `useTiltSignalLost`, `useAuth`) e
 * compõe os componentes puros. Regra #2: nada lê Supabase direto.
 */
import { useNavigate } from 'react-router-dom';
import { emergencyNavState } from '@/domain';
import { useAuth } from '@/hooks/useAuth';
import { useDamScope } from '@/hooks/useDamScope';
import { useTiltSignalLost } from '@/hooks/useTiltSignalLost';
import JFBrasilLogo from '@/ui/JFBrasilLogo';
import { useAlertStore } from '@/stores/alertStore';
import { StructureChips } from './StructureChips';
import { TiltLostBanner } from './TiltLostBanner';
import { EmergencyContactsButton } from './EmergencyContactsButton';
import SinoDeNotificacoes from './SinoDeNotificacoes';
import UserMenu from './UserMenu';

interface HeaderProps {
  /** Alterna a sidebar (estado vive no AppShell). */
  onToggleSidebar?: () => void;
}

export default function Header({ onToggleSidebar }: HeaderProps) {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const overall = useAlertStore((s) => s.overall);
  const alertActive = overall !== 'NORMAL';
  const { dams, isLoading: loadingScope, error: scopeError } = useDamScope();
  const tiltLost = useTiltSignalLost();

  return (
    <>
      <header className="flex items-center justify-between gap-2 min-[420px]:gap-3 px-3 min-[420px]:px-4 py-2 bg-jf-surface2 border-b border-jf-border shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onToggleSidebar}
            className="md:block hidden text-jf-muted hover:text-white"
            aria-label="Toggle sidebar"
          >
            ☰
          </button>
          <JFBrasilLogo height={24} width={82} className="hidden sm:block flex-shrink-0" />
          <div className="w-px h-6 bg-jf-border flex-shrink-0 hidden sm:block" />
          {/* Bloco de marca: min-w-0 + truncate para CEDER espaço antes dos chips A/B/C
              (GUPM-212). O nome some abaixo de `sm` (junto do logo) em vez de virar um
              "Geo…" cortado — prioridade: pills/botões nunca somem, a marca sim.
              Subtítulos/versão só em telas largas (`lg`). */}
          <div className="flex flex-col min-w-0">
            <strong className="text-white text-sm font-black leading-none truncate hidden sm:block">GeoGuard UPM</strong>
            <span className="text-[11px] text-jf-muted leading-none mt-0.5 truncate hidden lg:block">Monitoramento Geotécnico de Barragens</span>
            <span className="text-[11px] text-jf-muted leading-none mt-0.5 truncate hidden lg:block">Foscentro — Unidade Patos de Minas</span>
            <span className="text-[9px] text-jf-yellow/60 leading-none mt-0.5 hidden lg:block">v{__APP_VERSION__}</span>
          </div>
        </div>

        {/* Grupo da direita PROTEGIDO (shrink-0): chips A/B/C + emergência + contatos
            + menu de usuário nunca cedem/somem. Quem cede quando a tela aperta é a
            marca à esquerda (min-w-0 + truncate) — prioridade pedida pelo dono (GUPM-212). */}
        <div className="flex items-center gap-1.5 min-[420px]:gap-2 sm:gap-3 shrink-0">
          {/* Status por barragem (A/B/C) — no header-right, conforme protótipo v5.10.
              Estados de loading/erro do dam scope explícitos (frontend.md). */}
          {loadingScope ? (
            <span role="status" className="hidden sm:inline text-[11px] text-jf-dim animate-pulse">
              Carregando status…
            </span>
          ) : scopeError ? (
            <span
              role="status"
              title="Não foi possível carregar o status das barragens"
              className="hidden sm:inline text-[11px] text-jf-dim"
            >
              Status indisponível
            </span>
          ) : (
            <StructureChips dams={dams} />
          )}

          {/* Emergência: abre a TARP-EMG no M3 (GUPM-190 T5) — navega com a intenção
              de emergência no nav-state (mesmo padrão do auto-open). Não é escrita de dado. */}
          <button
            onClick={() => navigate('/tarp', { state: emergencyNavState() })}
            className={`px-2 min-[420px]:px-3 py-1.5 rounded-lg text-[11px] font-black text-white whitespace-nowrap transition-opacity ${alertActive ? 'bg-level-emergencia animate-pulse' : 'bg-slate-500 opacity-60'}`}
            aria-label="Acionar protocolo de emergência PAEBM"
          >
            <span aria-hidden>🆘</span>
            {/* Abaixo de 420px vira só 🆘 (o rótulo custa ~74px) — assim chips + contatos
                + menu cabem sem clipar em telas estreitas; o aria-label mantém a acessibilidade. */}
            <span className="hidden min-[420px]:inline"> EMERGÊNCIA</span>
          </button>

          {/* Sino (C-24): o que ainda pede ação, em qualquer tela. Mesmo lugar do V8 —
              antes dos contatos de emergência. */}
          {user && profile && <SinoDeNotificacoes profile={profile} />}

          <EmergencyContactsButton />

          {/* Menu de conta (GUPM-212): nome/email/perfil + TOTP + Sair no dropdown. */}
          {user && <UserMenu />}
        </div>
      </header>

      <TiltLostBanner visible={tiltLost} />
    </>
  );
}
